/**
 * TextBlock — renders markdown-formatted text from the AI.
 * Supports bold, italic, lists, line breaks, and inline code.
 */

import type { TextBlock as TextBlockType } from '../../../stores/CoPilotStore'

/** Simple markdown → JSX renderer (no heavy dependencies) */
function renderMarkdown(text: string): JSX.Element[] {
  const lines = text.split('\n')
  const elements: JSX.Element[] = []
  let listItems: string[] = []
  let listKey = 0

  function flushList() {
    if (listItems.length > 0) {
      elements.push(
        <ul key={`list-${listKey++}`} className="list-disc pl-5 space-y-1 text-[14px] text-[#3F3F46]">
          {listItems.map((item, i) => (
            <li key={i}>{renderInline(item)}</li>
          ))}
        </ul>
      )
      listItems = []
    }
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    const trimmed = line.trim()

    // Bullet list item
    if (trimmed.startsWith('- ') || trimmed.startsWith('* ') || trimmed.match(/^\d+\.\s/)) {
      const content = trimmed.replace(/^[-*]\s|^\d+\.\s/, '')
      listItems.push(content)
      continue
    }

    // Flush any pending list
    flushList()

    // Empty line
    if (!trimmed) {
      continue
    }

    // Headings: #### → h4, ### → h3, ## → h2, # → h1
    const headingMatch = trimmed.match(/^(#{1,4})\s+(.+)$/)
    if (headingMatch) {
      const level = headingMatch[1].length
      const content = headingMatch[2]
      const Tag = `h${level}` as 'h1' | 'h2' | 'h3' | 'h4'
      const styles: Record<string, string> = {
        h1: 'text-[18px] font-bold text-[#18181B] mt-3',
        h2: 'text-[16px] font-bold text-[#18181B] mt-2',
        h3: 'text-[15px] font-semibold text-[#18181B] mt-2',
        h4: 'text-[14px] font-semibold text-[#3F3F46] mt-1',
      }
      elements.push(
        <Tag key={`h-${i}`} className={styles[Tag]}>
          {renderInline(content)}
        </Tag>
      )
      continue
    }

    // Regular paragraph
    elements.push(
      <p key={`p-${i}`} className="text-[14px] text-[#3F3F46] leading-relaxed">
        {renderInline(trimmed)}
      </p>
    )
  }

  flushList()
  return elements
}

/** Render inline markdown: **bold**, *italic*, `code` */
function renderInline(text: string): (string | JSX.Element)[] {
  const parts: (string | JSX.Element)[] = []
  // Match **bold**, *italic*, `code`
  const regex = /(\*\*(.+?)\*\*|\*(.+?)\*|`(.+?)`)/g
  let lastIndex = 0
  let match: RegExpExecArray | null
  let key = 0

  while ((match = regex.exec(text)) !== null) {
    // Add text before match
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index))
    }

    if (match[2]) {
      // **bold**
      parts.push(
        <strong key={key++} className="font-semibold text-[#18181B]">
          {match[2]}
        </strong>
      )
    } else if (match[3]) {
      // *italic*
      parts.push(
        <em key={key++} className="italic">
          {match[3]}
        </em>
      )
    } else if (match[4]) {
      // `code`
      parts.push(
        <code key={key++} className="px-1 py-0.5 bg-[#F5F5F5] rounded text-[12px] font-mono text-[#52525B]">
          {match[4]}
        </code>
      )
    }

    lastIndex = match.index + match[0].length
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex))
  }

  return parts.length > 0 ? parts : [text]
}

export function TextBlock({ block }: { block: TextBlockType }) {
  return <div className="space-y-2">{renderMarkdown(block.content)}</div>
}
